import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';
import { CatalogVm, ContainerVm, StorefrontVm, TabVm } from 'qp-ui-interface';
import { Observable, from } from 'rxjs';
import { DataService } from '../../data.service';
import { CatalogService } from '../../storefront/catalog.service';
import { BookmarkService } from '../services/bookmark.service';
import { TabService } from '../services/tabs/tab.service';

@Injectable()
export class StorefrontListResolver implements Resolve<unknown> {
  constructor(
    private readonly catalogService: CatalogService,
    private readonly tabService: TabService,
    private readonly dataService: DataService,
    private readonly router: Router,
    private readonly bookmarkService: BookmarkService
  ) {}

  resolve(_route: ActivatedRouteSnapshot): Observable<StorefrontVm> {
    this.startNRTimeToLoadStorefront();
    return from(this.fetchTabsAndStorefront(_route));
  }

  private async fetchTabsAndStorefront(
    _route: ActivatedRouteSnapshot
  ): Promise<StorefrontVm> {
    let actualTabContent: TabVm;
    let catalogVm: CatalogVm;
    let personalisationData: ContainerVm | null = null;
    const newTabId = _route.params?.id;

    if (_route.data.isStaticTab) {
      const storefrontData1 = { containers: [], totalContainers: 0 };
      return Promise.resolve(storefrontData1);
    }

    try {
      if (_route.data.isStorefront) {
        const storedTabId = this.tabService
          .getTabs()
          .find((tabvm) => tabvm.containers?.length)?.id;

        // Always fetch fresh data when navigating back from detail
        const isBackNavigation = window.history.state?.navigationId > 1;
        if (newTabId !== storedTabId) {
          catalogVm = await this.catalogService.getStorefrontData(newTabId);
          actualTabContent = catalogVm.tabVMs.find((t) => t.id === newTabId);
          if (!actualTabContent) {
            // If tab not found, get the first tab with containers
            actualTabContent = catalogVm.tabVMs.find(
              (t) => t.containers?.length
            );
          }
          this.tabService.setTabs(catalogVm.tabVMs);
        } else {
          actualTabContent = this.tabService
            .getTabs()
            .find((tabvm) => tabvm.containers);
        }

        // If still no tab content, fetch fresh data
        if (!actualTabContent) {
          catalogVm = await this.catalogService.getStorefrontData(newTabId);
          actualTabContent =
            catalogVm.tabVMs.find((t) => t.id === newTabId) ||
            catalogVm.tabVMs.find((t) => t.containers?.length);
          this.tabService.setTabs(catalogVm.tabVMs);
        }
      } else if (_route.data.isStorefrontTv) {
        catalogVm = await this.catalogService.getStorefrontData();
        actualTabContent = catalogVm.tabVMs.find((tabVm: TabVm) => tabVm.name);
        if (!actualTabContent) {
          actualTabContent = catalogVm.tabVMs.find((t) => t.containers?.length);
        }
        this.tabService.setTabs(catalogVm.tabVMs);
      }

      this.dataService.activeTab =
        this.tabService.getCurrentTab()?.id || actualTabContent?.id;
      this.dataService.homeTab =
        this.tabService.getTabs()?.[0]?.id || actualTabContent?.id;

      const tab = '' + this.dataService.activeTab;
      this.tabService.currentTabId = newTabId || tab;
      this.tabService.currentTabName = this.tabService
        .getTabs()
        .find((tabvm) => tabvm.id === this.tabService.currentTabId)?.name;
      if (
        _route.data.isStorefront &&
        this.tabService.currentTabId === this.dataService.homeTab
      ) {
        personalisationData =
          await this.bookmarkService.getQpBookmarksContainer();
      }

      const navigation = this.router.getCurrentNavigation();
      const state = navigation?.extras?.state;
      if (!_route.params?.id && state?.landingPath) {
        this.router.navigate([state.landingPath]);
      } else if (!_route.params?.id) {
        this.router.navigate([tab], { state: { tabId: tab } });
      }

      // Ensure we have valid data
      if (!actualTabContent) {
        throw new Error('No tab content available');
      }

      const storefrontData = {
        containers: actualTabContent.containers || [],
        totalContainers: actualTabContent.tabPagination?.count || 0,
        personalisationData,
      };

      return Promise.resolve(storefrontData);
    } catch (error) {
      return Promise.resolve({
        containers: [],
        totalContainers: 0,
        personalisationData: null,
      });
    }
  }

  private startNRTimeToLoadStorefront(): void {
    sessionStorage.setItem('storefrontStart', '' + new Date().getTime());
  }
}
